import React, {
  useState, useEffect, useCallback, useMemo, Dispatch, SetStateAction,
} from 'react';
import {
  useSelector,
} from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  Table, Icon, Label, Button, Popup, Modal, Message, Divider,
} from 'semantic-ui-react';
import util from 'utils/utils';
import api from 'api';
import toast from 'react-hot-toast';
import reportsApi from 'utils/reports';
import moment from 'moment';
import useTheme from 'theme/useTheme';

import {
  Other,
} from 'simplydo/interfaces';
import { HandledIdeaPreview as IdeaPreview } from 'components/lib/Ideas';
import { FakeLink, ThemedButton } from 'components/lib/UI';
import ProductTour from 'components/lib/ProductTour';

export const AssessmentClosureInfo = ({ ideaTemplate }: { ideaTemplate?: Other.IIdeaTemplate }) => {
  if (!ideaTemplate) return null;
  const { assessmentClosed = false, assessmentDeadline } = ideaTemplate;
  if (assessmentClosed) {
    return (
      <Message warning>
        <Message.Header>Assessment closed</Message.Header>
        <p>
          Assessment is now closed for this challenge. You can continue to view your assigned ideas and previous assessments. If you think this is in error, please contact an administrator.
        </p>
      </Message>
    );
  }
  if (assessmentDeadline) {
    return (
      <Message info>
        <Message.Header>Assessment deadline</Message.Header>
        <p>
          Assessment for this challenge has a deadline at <b>{moment(assessmentDeadline).format('HH:mm on DD/MM/YY')}</b>. You cannot edit or submit assessments after this date.
        </p>
      </Message>
    );
  }
  return null;
};

type IChallengeYourAssessments = {
  challenge: Other.IChallenge,
  getAssignedAssessments: () => void,
  myAssignedAssessments: Other.IIdeaAssignment[],
  setMyAssignedAssessments: Dispatch<SetStateAction<Other.IIdeaAssignment[]>>,
  demoingTour?: string,
}

function ChallengeYourAssessments({
  getAssignedAssessments, myAssignedAssessments, setMyAssignedAssessments, challenge, demoingTour,
}: IChallengeYourAssessments) {
  const searchParams = new URLSearchParams(window.location.search);
  const user = useSelector((state: { user: Other.IUserMe | null }) => state?.user);
  const urlOrganisation = useSelector((state: { organisations: { urlOrganisation: Other.IOrganisation | null } }) => state?.organisations?.urlOrganisation);
  const [previewId, setPreviewId] = useState(searchParams.has('idea') ? searchParams.get('idea') : '');
  const [assessmentEdited, setAssessmentEdited] = useState(false);
  const [tourCheckLoaded, setTourCheckLoaded] = useState(false);
  const [link, setLink] = useState();
  const [reportDownloaded, setReportDownloaded] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState('');
  const shouldShowTour = useMemo(() => (!!challenge?._id && !!myAssignedAssessments.length), [challenge, myAssignedAssessments]);
  const tourIdea = useMemo(() => (myAssignedAssessments.length ? myAssignedAssessments[0]._id : ''), [myAssignedAssessments]);
  const { ideaTemplateData = { assessmentClosed: false } } = challenge;
  const { assessmentClosed = false } = ideaTemplateData;
  const challengeId = challenge?._id;
  const userId = user?._id;
  const history = useHistory();
  const theme = useTheme();

  useEffect(() => {
    if (previewId) setAssessmentEdited(false);
  }, [previewId]);

  // If we are about to run the assessment tour, we do not display the idea preview
  const getTour = useCallback(() => {
    if (user?.organisation?._id || urlOrganisation) {
      api.organisations.getProductTour(user?.organisation?._id || urlOrganisation, 'assessment', ({ success, userHasCompletedTour }) => {
        if (success && (!userHasCompletedTour || demoingTour === 'assessment')) {
          history.replace({ search: '' });
          setPreviewId('');
        }
        setTourCheckLoaded(true);
      }, () => setTourCheckLoaded(true));
    } else {
      setTourCheckLoaded(true);
    }
  }, [user, demoingTour, urlOrganisation, history]);

  useEffect(() => {
    getTour();
  }, [getTour]);

  const onModalClose = useCallback(() => {
    history.replace({ search: '' });
    setPreviewId('');
    if (assessmentEdited) getAssignedAssessments();
  }, [assessmentEdited, setPreviewId, getAssignedAssessments, history]);

  const submitAssessment = (ideaId) => {
    util.confirm('Ready to submit your assessment?', 'Once submitted you will not be able to change your assessment of this idea.')
      .then(() => api.ideas.submitAssessment(ideaId, ({ idea: updatedIdea }) => {
        toast.success('Assessment submitted');
        setMyAssignedAssessments((prevState) => prevState.map((ideaAssessment) => {
          if (ideaAssessment._id !== ideaId) return ideaAssessment;
          return { ...ideaAssessment, ...updatedIdea };
        }));
      }, (err) => {
        toast.error(`Unable to submit your assessment: ${err.message}`);
      })).catch(() => { });
  };

  const submitAllAssessments = useCallback(() => {
    util.confirm(
      'Ready to submit your assessments?',
      'Once submitted you will not be able to change any of your assessments in this challenge. Only filled in assessments will be submitted.',
    )
      .then(() => {
        api.challenges.submitAllAssessments(challengeId, ({ submittedAssessmentIdeaIds }) => {
          toast.success('Assessments submitted');
          setMyAssignedAssessments((prevState) => prevState.map((ideaAssessment) => {
            if (submittedAssessmentIdeaIds.indexOf(ideaAssessment._id) > -1) {
              return ({
                ...ideaAssessment,
                assessments: ideaAssessment.assessments.map((prev) => ({ ...prev, isSubmitted: true })),
              });
            } return ideaAssessment;
          }));
        }, (err) => toast.error(`Unable to submit your assessment: ${err.message}`));
      }).catch(() => { });
  }, [challengeId, setMyAssignedAssessments]);

  const downloadReport = () => {
    reportsApi.token = api.token;
    api.reports.individualAssessorReport(challenge._id, (c) => {
      setLink(c.link);
      setReportDownloaded(true);
    }, () => { toast.error('Error unable to generate report'); });
  };

  const downloadPdf = useCallback((ideaId) => {
    setDownloadingPdf(ideaId);
    const downloadingIdea = myAssignedAssessments.find((idea) => idea._id === ideaId);
    util.downloadIdeaPdf(downloadingIdea)
      .then(() => {
        setDownloadingPdf('');
      })
      .catch((err) => {
        toast.error(err);
        setDownloadingPdf('');
      })
  }, [myAssignedAssessments]);

  /* Note that myAssignedAssessments is a list of ideas, each with an assessment object, which might contain an assessments array. Each entry in the assessments array is an assessment made by an individual assessor, which might contain a (deeper) assessment object, which references individual idea sections.
   * Example myAssignedAssessments: [
   * {
   *    name: 'Idea name',
   *    _id: 'IdeaID',
   *    assessmentScore: 75,
   *    assessmentSubmittedScore: 75,
   *    assessments: [ // array of assessor's assessments
   *      {
   *        score: 75, // total calculated score across this assessor based on total available
   *        assessorUser: userObject,
   *        assessment: {
   *          'section1ID': {
   *            score: 15, // assessor's score for this section,
   *            notes: 'Notes for this section'
   *          }, ...
   *        },
   *      }, ...
   *    ]
   *  }, ...
   * ]
   *
   * Note that whilst other assessor's assessments may be present in the DB and their names included in the array, if scoped to a single assessor then the total calcualted score will only take into account the scoped assessment.
  */
  const hasUnsubmittedAssessments = myAssignedAssessments.find((assessment) => assessment.assessments.find((x) => x.isSubmitted !== true));
  return (
    <>
      {/* @ts-ignore */}
      <IdeaPreview
        ideaId={tourCheckLoaded && previewId}
        onAssessmentEdited={() => setAssessmentEdited(true)}
        closeModal={() => onModalClose()}
      />
      <ProductTour
        tour='assessment'
        isReady={shouldShowTour}
        customTourFunctions={{
          openIdea: () => setPreviewId(tourIdea),
          closeIdea: () => {
            onModalClose();
          },
        }}
        onComplete={() => setPreviewId('')}
      />
      {!user
        ? (
          <Message>
            <h3>Please login to view this page</h3>
            <p>Register or login to your SimplyDo account to view your assessments for this challenge.</p>
            <Divider section />
            <Button as={Link} to={`/login?then=/challenges/${challenge?._id}/assessments`} primary>Login or register</Button>
          </Message>
        ) : (
          <>
            <div className='assessment-tour-step-1'>
              <div
                style={{
                  display: 'flex', width: '100%', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 10,
                }}
              >
                <h3 style={{ display: 'block', margin: 0 }}>Your assessments</h3>
              </div>

              <Table unstackable basic='very'>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Idea</Table.HeaderCell>
                    <Table.HeaderCell collapsing>
                      Your total score
                      <Popup
                        position='top right'
                        trigger={(<Icon name='question circle' />)}
                        content='Automatically calculated based on your assessment & predefined section weightings'
                      />
                    </Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Conflict</Table.HeaderCell>
                    {!theme.sizes.isMobile && <Table.HeaderCell />}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {myAssignedAssessments?.map((a, index) => {
                    const myAssessment = a.assessments?.find((ideaAssessment) => ideaAssessment.user === userId);
                    return (
                      <Table.Row key={a._id} className={'assessment-tour-step-2'}>
                        <Table.Cell>
                          {/* @ts-ignore */}
                          <FakeLink onClick={() => setPreviewId(a._id)}>{a.name}</FakeLink>
                        </Table.Cell>
                        <Table.Cell collapsing>
                          {myAssessment?.conflictOfInterest?.hasConflictOfInterest && myAssessment?.isSubmitted
                            ? 'N/A'
                            : <>{Math.round(myAssessment?.score || 0)} %</>
                          }

                        </Table.Cell>
                        <Table.Cell collapsing>
                          {myAssessment?.isSubmitted
                            ? <Label size='small' color='green'>Submitted</Label>
                            : <Label size='small' color='orange'>Not submitted</Label>
                          }
                        </Table.Cell>
                        <Table.Cell collapsing>
                          {myAssessment?.conflictOfInterest?.hasConflictOfInterest
                            ? <Label size='small' color='red' basic>Conflict of interest</Label>
                            : null
                          }
                        </Table.Cell>
                        {!theme.sizes.isMobile
                          && <Table.Cell collapsing>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              {!myAssessment?.isSubmitted && !assessmentClosed
                                ? <>
                                  {myAssessment?.conflictOfInterest?.hasConflictOfInterest
                                    ? <Popup trigger={<Button primary size='tiny' content='Submit assessment' onClick={() => submitAssessment(a._id)} />} content='Conflict of interest declared. When you submit this assessment, all assessment scores/notes will be cleared.' />
                                    : <Button size='tiny' primary content='Submit assessment' onClick={() => submitAssessment(a._id)} />}
                                </> : null
                              }
                              <Button
                                size='tiny'
                                basic
                                content='Download idea PDF'
                                icon='file pdf'
                                loading={downloadingPdf === a._id}
                                onClick={() => downloadPdf(a._id)}
                              />
                              <Button size='tiny' basic content='View idea' className={index === 0 ? 'assessment-tour-step-3' : null} icon='eye' onClick={() => setPreviewId(a._id)} />
                            </div>
                          </Table.Cell>
                        }
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
            <div className='assessment-tour-step-5'>
              {/* @ts-ignore */}
              <AssessmentClosureInfo ideaTemplate={ideaTemplateData} />
            </div>
            <div style={{
              display: 'flex', justifyContent: 'flex-end', width: '100%', marginTop: 5,
            }}>
              <Popup trigger={<Button size='tiny' secondary basic content='Download summary report' icon='file excel' onClick={() => downloadReport()} />}
                content='Generate an excel report containing a summary of your assessments' />
              {hasUnsubmittedAssessments && <Button size='tiny' icon='check' primary content='Submit all assessments' className='assessment-tour-step-6' onClick={() => submitAllAssessments()} />}
            </div>
            <Modal small open={reportDownloaded}>
              <Modal.Header>
                Report Generated
              </Modal.Header>
              <Modal.Content>
                <p>Your assessment report was generated successfully.</p>
              </Modal.Content>
              <Modal.Actions>
                <Button content='Cancel' onClick={() => setReportDownloaded(false)} />
                {/* @ts-ignore */}
                <ThemedButton as='a' secondary icon='file pdf' onClick={() => setReportDownloaded(false)} href={link} content='Download report' />
              </Modal.Actions>
            </Modal>
          </>
        )
      }
    </>
  );
}

export default ChallengeYourAssessments;
